import type { StaffRole } from '../types/auth';

/**
 * PRO-1700 — first-gate router (category, not site).
 *
 * Pure. The six Central Services labels are the exact PRO-1550 / `0170` strings
 * (em dash). School-family categories — including Site — Spot Salary — stay
 * with the site Operations Director. Mixed or unknown fail closed.
 */

export type AtrFunctionCode =
  | 'business_development'
  | 'estates'
  | 'finance'
  | 'it'
  | 'people'
  | 'quality';

export type AtrFirstGateRoute =
  | { readonly kind: 'ops_director' }
  | {
      readonly kind: 'function_head';
      readonly functionCode: AtrFunctionCode;
      readonly staffRole: StaffRole;
    }
  | { readonly kind: 'fail_closed' };

export const CS_CATEGORY_TO_FUNCTION: Readonly<
  Record<string, { readonly functionCode: AtrFunctionCode; readonly staffRole: StaffRole }>
> = {
  'Central Services — Business Development': {
    functionCode: 'business_development',
    staffRole: 'BUSINESS_DEVELOPMENT_DIRECTOR',
  },
  'Central Services — Estates': {
    functionCode: 'estates',
    staffRole: 'ESTATES_DIRECTOR',
  },
  'Central Services — Finance': {
    functionCode: 'finance',
    staffRole: 'FINANCE_DIRECTOR',
  },
  'Central Services — IT': {
    functionCode: 'it',
    staffRole: 'IT_DIRECTOR',
  },
  'Central Services — People': {
    functionCode: 'people',
    staffRole: 'PEOPLE_DIRECTOR',
  },
  'Central Services — Quality': {
    functionCode: 'quality',
    staffRole: 'QUALITY_DIRECTOR',
  },
};

export const FUNCTION_HEAD_ROLES: readonly StaffRole[] = [
  'BUSINESS_DEVELOPMENT_DIRECTOR',
  'ESTATES_DIRECTOR',
  'FINANCE_DIRECTOR',
  'IT_DIRECTOR',
  'PEOPLE_DIRECTOR',
  'QUALITY_DIRECTOR',
];

export const CS_CATEGORY_LABELS: readonly string[] = Object.keys(CS_CATEGORY_TO_FUNCTION);

export function functionHeadEnabled(): boolean {
  return process.env.UCP_ATR_FUNCTION_HEAD_ENABLED === 'true';
}

/**
 * PRO-1910 / ADR-079 D7. Honoured ONLY while Function Head routing is on: the
 * Central Services unit has no Operations Director, so with A2 off a request
 * raised there would have no first-gate actor. Fail-safe deny.
 *
 * This is the single definition of the rule and the default when no resolved
 * value is supplied. On the API path the resolved value is threaded in instead
 * (`resolveAtrCentralServicesEnabled`), so the test override is honoured.
 */
export function centralServicesEnabled(): boolean {
  return process.env.UCP_ATR_CENTRAL_SERVICES_ENABLED === 'true' && functionHeadEnabled();
}

export function routeFirstGate(categories: readonly string[]): AtrFirstGateRoute {
  if (categories.length === 0) {
    return { kind: 'fail_closed' };
  }

  const mapped = categories.map((category) => CS_CATEGORY_TO_FUNCTION[category] ?? null);
  const anyUnknownCsShape = categories.some(
    (category) => category.startsWith('Central Services') && CS_CATEGORY_TO_FUNCTION[category] == null,
  );
  if (anyUnknownCsShape) {
    return { kind: 'fail_closed' };
  }

  const cs = mapped.filter(
    (entry): entry is { functionCode: AtrFunctionCode; staffRole: StaffRole } => entry != null,
  );
  const schoolFamily = mapped.length - cs.length;

  if (cs.length === 0) {
    return { kind: 'ops_director' };
  }
  if (schoolFamily > 0) {
    return { kind: 'fail_closed' };
  }

  const first = cs[0]!;
  const mixed = cs.some(
    (entry) => entry.functionCode !== first.functionCode || entry.staffRole !== first.staffRole,
  );
  if (mixed) {
    return { kind: 'fail_closed' };
  }
  return { kind: 'function_head', functionCode: first.functionCode, staffRole: first.staffRole };
}

export function csCategoryForFunction(functionCode: AtrFunctionCode): string {
  const found = Object.entries(CS_CATEGORY_TO_FUNCTION).find(
    ([, value]) => value.functionCode === functionCode,
  );
  return found?.[0] ?? '';
}

export function functionCodesForRoles(roles: readonly StaffRole[]): AtrFunctionCode[] {
  const codes = new Set<AtrFunctionCode>();
  for (const [ , value] of Object.entries(CS_CATEGORY_TO_FUNCTION)) {
    if (roles.includes(value.staffRole)) {
      codes.add(value.functionCode);
    }
  }
  return [...codes];
}
